import { nearestPowerOfTwo } from './helpers'

/**
 * Hermite resize - fast image resize/resample using Hermite filter.
 */
const resampleSingle = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  resizeCanvas = true,
) => {
  const widthSource = canvas.width
  const heightSource = canvas.height

  width = Math.round(width)
  height = Math.round(height)

  const widthRatio = widthSource / width
  const heightRatio = heightSource / height
  const halfWidthRatio = Math.ceil(widthRatio / 2)
  const halfHeightRatio = Math.ceil(heightRatio / 2)

  const context = canvas.getContext('2d')

  if (context === null) {
    throw new Error('Failed to initialize frame viewer image canvas context.')
  }

  const sourceImageData = context.getImageData(0, 0, widthSource, heightSource)
  const targetImageData = context.createImageData(width, height)
  const sourceData = sourceImageData.data
  const targetData = targetImageData.data

  for (let j = 0; j < height; j += 1) {
    for (let i = 0; i < width; i += 1) {
      const targetX = (i + j * width) * 4

      let weight = 0
      let weights = 0
      let weightsAlpha = 0
      let gxRed = 0
      let gxGreen = 0
      let gxBlue = 0
      let gxAlpha = 0

      const centerY = (j + 0.5) * heightRatio
      const yyStart = Math.floor(j * heightRatio)
      const yyStop = Math.ceil((j + 1) * heightRatio)

      for (let yy = yyStart; yy < yyStop; yy += 1) {
        const dy = Math.abs(centerY - (yy + 0.5)) / halfHeightRatio
        const centerX = (i + 0.5) * widthRatio
        const w0 = dy * dy //pre-calc part of w
        const xxStart = Math.floor(i * widthRatio)
        const xxStop = Math.ceil((i + 1) * widthRatio)

        for (let xx = xxStart; xx < xxStop; xx += 1) {
          const dx = Math.abs(centerX - (xx + 0.5)) / halfWidthRatio
          const w = Math.sqrt(w0 + dx * dx)

          if (w >= 1) {
            // Pixel too far
            continue
          }

          // Hermite filter
          weight = 2 * w * w * w - 3 * w * w + 1
          const positionX = 4 * (xx + yy * widthSource)

          // Alpha
          gxAlpha += weight * sourceData[positionX + 3]
          weightsAlpha += weight

          // Colors
          if (sourceData[positionX + 3] < 255) {
            weight = (weight * sourceData[positionX + 3]) / 250
          }

          gxRed += weight * sourceData[positionX]
          gxGreen += weight * sourceData[positionX + 1]
          gxBlue += weight * sourceData[positionX + 2]
          weights += weight
        }
      }

      targetData[targetX] = gxRed / weights
      targetData[targetX + 1] = gxGreen / weights
      targetData[targetX + 2] = gxBlue / weights
      targetData[targetX + 3] = gxAlpha / weightsAlpha
    }
  }

  // Clear and resize canvas
  if (resizeCanvas) {
    canvas.width = width
    canvas.height = height
  }

  if (!resizeCanvas) {
    context.clearRect(0, 0, widthSource, heightSource)
  }

  // Draw
  context.putImageData(targetImageData, 0, 0)
}

/**
 * Calculates a square canvas size to fit any image with optimal cropping.
 */
const getCanvasSizeToFit = (
  artworkWidth: number,
  artworkHeight: number,
  imageWidth: number,
  imageHeight: number,
): number => {
  // If image is square, use its size
  if (imageWidth === imageHeight) {
    return imageWidth
  }

  // If frame is stretched one way but the image - another way, use image's shortest dimension
  if (
    (imageWidth > imageHeight && artworkWidth <= artworkHeight) ||
    (imageWidth < imageHeight && artworkWidth >= artworkHeight)
  ) {
    return Math.min(imageWidth, imageHeight)
  }

  // If both image and frame are stretched the same way, but frame is stretched more, use image's longest dimension
  const imageRatio = imageWidth / imageHeight
  const artRatio = artworkWidth / artworkHeight
  if (
    (imageRatio > 1 && imageRatio < artRatio) ||
    (imageRatio < 1 && imageRatio > artRatio)
  ) {
    return Math.max(imageWidth, imageHeight)
  }

  // If frame is stretched less than the image, calculate necessary canvas size
  const absoluteFameRatio =
    Math.max(artworkWidth, artworkHeight) /
    Math.min(artworkWidth, artworkHeight)
  const imageMinSize = Math.min(imageWidth, imageHeight)

  return imageMinSize * absoluteFameRatio
}

/**
 * Generates texture URL from image.
 */
export const generateTextureUrlFromImage = async (
  imageUrl: string,
  containerWidth: number,
  containerHeight: number,
): Promise<string> => {
  return new Promise((resolve) => {
    const image = new Image()

    image.onload = () => {
      const offScreenCanvas = document.createElement('canvas')

      // Place the image in the middle of a square canvas
      const canvasSize = getCanvasSizeToFit(
        containerWidth,
        containerHeight,
        image.width,
        image.height,
      )
      offScreenCanvas.width = canvasSize
      offScreenCanvas.height = canvasSize
      const context = offScreenCanvas.getContext('2d')

      if (context === null) {
        throw new Error(
          'Failed to initialize frame viewer image canvas context.',
        )
      }

      context.drawImage(
        image,
        canvasSize / 2 - image.width / 2,
        canvasSize / 2 - image.height / 2,
      )

      // Downsize the image
      const potCanvasSize = nearestPowerOfTwo(canvasSize)

      if (potCanvasSize != canvasSize) {
        resampleSingle(offScreenCanvas, potCanvasSize, potCanvasSize)
      }

      resolve(offScreenCanvas.toDataURL('image/png'))
    }

    image.onerror = () => {
      // On error use original picture
      resolve(imageUrl)
    }

    image.crossOrigin = 'Anonymous'
    image.src = imageUrl
  })
}
